import type { BuildConfig } from './build.types';
import type { EkkoApp } from './contract.types';

interface TrustedTypePolicyLike {
  createScriptURL(url: string): unknown;
}

interface TrustedTypesLike {
  createPolicy(
    name: string,
    rules: { createScriptURL(url: string): string },
  ): TrustedTypePolicyLike;
}

export const POLICY_NAME = 'ekko-checkout';

// An unrecognised token parses to empty metadata, and the browser then skips the check.
const SRI = /^sha(256|384|512)-[A-Za-z0-9+/]+={0,2}$/;

export interface AppLoader {
  /** Starts the download once; every later call shares it. */
  load(): Promise<EkkoApp>;
  /** Adds one script under the app's prefix the way the app itself was added. */
  loadScript(url: string, integrity: string): Promise<void>;
}

export function createAppLoader(config: BuildConfig, nonce: string): AppLoader {
  let pending: Promise<EkkoApp> | undefined;
  // One attempt per page: a trusted-types directive without 'allow-duplicates'
  // refuses a second policy of the same name, and each refusal is a CSP report.
  let tried = false;
  let policy: TrustedTypePolicyLike | null = null;

  // The URL as the browser will fetch it, so a "../" cannot climb out of the
  // prefix; empty when it is not under the prefix.
  function ours(url: string): string {
    try {
      const href = new URL(url, document.baseURI).href;
      return href.indexOf(config.appPrefix) === 0 ? href : '';
    } catch {
      return '';
    }
  }

  // A merchant can allowlist policy names without enforcing Trusted Types, so a
  // refused policy falls back to the plain URL; only the src assignment decides.
  function trustedUrl(url: string): unknown {
    const trustedTypes = (window as unknown as { trustedTypes?: TrustedTypesLike }).trustedTypes;
    if (!trustedTypes) return url;
    if (!tried) {
      tried = true;
      try {
        policy = trustedTypes.createPolicy(POLICY_NAME, {
          createScriptURL(candidate) {
            if (ours(candidate)) return candidate;
            throw new TypeError(`[ekko-checkout] ${candidate} is not ours`);
          },
        });
      } catch {
        // Refused: the plain URL it is.
      }
    }
    try {
      return policy ? policy.createScriptURL(url) : url;
    } catch {
      return url;
    }
  }

  function inject(url: string, integrity: string, check?: () => string): Promise<void> {
    return new Promise((resolve, reject) => {
      let directive = '';
      const onViolation = (event: SecurityPolicyViolationEvent) => {
        if (event.blockedURI === url) directive = event.effectiveDirective;
      };
      document.addEventListener('securitypolicyviolation', onViolation);

      const script = document.createElement('script');
      // A failed script goes too, so a retry starts from a fresh element.
      const settle = (message?: string) => {
        document.removeEventListener('securitypolicyviolation', onViolation);
        if (!message) return resolve();
        script.remove();
        reject(new Error(message));
      };

      script.addEventListener('error', () =>
        settle(
          directive
            ? `CSP ${directive} blocked ${url}: allow ${config.cdn} in ${directive}, or nonce core.js and add 'strict-dynamic'.`
            : `${url} did not load: allow ${config.cdn} in script-src, and do not rewrite it (its integrity is checked).`,
        ),
      );
      script.addEventListener('load', () => settle(check?.()));

      script.async = true;
      script.crossOrigin = 'anonymous';
      if (integrity) script.integrity = integrity;
      if (nonce) script.nonce = nonce;
      try {
        // A TrustedScriptURL when Trusted Types is enforced, else the plain string.
        (script as { src: unknown }).src = trustedUrl(url);
      } catch {
        settle(`require-trusted-types-for refused ${url}: add ${POLICY_NAME} to trusted-types.`);
        return;
      }
      (document.head || document.documentElement).appendChild(script);
    });
  }

  const registered = () => window.__ekkoCheckout?.[config.appBuild];

  function loadApp(): Promise<EkkoApp> {
    return inject(config.appUrl, config.appIntegrity, () => {
      const app = registered();
      if (!app || typeof app.mount !== 'function') return `${config.appUrl} did not register ${config.appBuild}.`;
      return app.contract !== 1 ? `app ${config.appBuild} is not contract 1.` : '';
    }).then(() => registered()!);
  }

  return {
    load() {
      if (!pending) {
        pending = loadApp();
        // Every init attaches its own handler; this one keeps a failure that
        // happens before any init from being reported as unhandled, and lets
        // the next init try again.
        pending.catch(() => (pending = undefined));
      }
      return pending;
    },
    loadScript(url, integrity) {
      const href = ours(url);
      if (!href) return Promise.reject(new Error(`${url} is not under ${config.appPrefix}.`));
      // A core that checks the app's integrity checks every part of it too.
      if (config.appIntegrity && !SRI.test(integrity)) {
        return Promise.reject(new Error(`${url} has no valid integrity.`));
      }
      return inject(href, integrity);
    },
  };
}
